import { Palette } from '@mui/material'
import { createContext, useEffect, useState } from 'react'
import { GlobalStyles } from 'tss-react/esm'
import { red } from '@mui/material/colors'
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { ThemeModes } from '../../enums'


declare interface VesselColorToken  {main: string, light?: string, dark?: string, contrastText?: string}

declare module '@mui/material/styles' {

  interface VesselPalette extends Palette {
    complementaryBlue: VesselColorToken
    paperOnPaper: VesselColorToken
  }
  interface Theme {
    themeMode?: ThemeModes
    setTheme?: (...args) => void
    palette: VesselPalette
  }
  interface BreakpointOverrides {
    xs: true // removes the `xs` breakpoint
    sm: true
    md: true
    lg: true
    xl: true
    xxl: true
  }
}
export const ThemeContext = createContext({
  themeMode: ThemeModes.Light,
  setTheme: (...args) => {},
})

export const ThemeContextProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState<any>()

  const theme = createTheme({
    typography: {
      fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      caption: {
        color: '#5f6368',
        fontSize: 14,
        fontWeight: 500,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 2400,
      },
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          // Name of the component ⚛️ / style sheet
          tooltip: {
            // Name of the rule
            fontSize: 14, // Some CSS
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
          },
          contained: {
            boxShadow: '0px 10px 20px -6px rgb(0 0 0 / 12%)',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: '#00304A',
            color: 'white',
          },
          indicator: {
            backgroundColor: '#21B573',
            height: '4px',
            marginBottom: 2,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: 'white !important',
          },
        },
      },
    },
    palette: {
      mode: themeMode,
      primary: {
        main: '#00304A',
        dark: '#00304A',
        contrastText: '#F2F2F2',
      },
      secondary: {
        main: '#21B573',
        dark: '#02799c',
      },
      error: {
        main: '#b5423e',
        dark: red[900],
      },
      background: {
        main: '#eee',
        dark: '#303030',
      },
      paperOnPaper: {
        main: '#cbcbcb',
      },
      divider: '#e6e6e6',
      hover: {
        main: '#e8f0fe',
        dark: '#e6e6e6a0',
      },
      complementaryBlue: {
        main: '#00526b36',
        light: '#0cc6ff',
        dark: '#09169e',
        contrastText: '#ffffff',
      },
      ...(themeMode === 'dark' && {
        primary: {
          main: '#02799c',
          dark: ' #00304A',
          contrastText: '#F2F2F2',
        },
       
        secondary: {
          main: '#02799c',
        },
        divider: '#e6e6e6',
        error: {
          main: red[900],
        },
        background: {
          main: '#303030',
        },
        hover: {
          main: '#e6e6e6a0',
        },
        paperOnPaper: {
          main: '#cbcbcb',
        },
        complementaryBlue: {
          main: '#00526b36',
          light: '#0cc6ff',
          dark: '#09169e',
          contrastText: '#ffffff',
        },
      }),
    },
  })
  useEffect(() => {
    const themeMode = localStorage.getItem('theme') || ThemeModes.Light
    setThemeMode(themeMode)
  }, [])

  const setTheme = newTheme => {
    localStorage.setItem('theme', newTheme)
    setThemeMode(newTheme)
  }

  return (
    <ThemeContext.Provider value={{ themeMode, setTheme }}>
      <GlobalStyles
        styles={{
          body: {
            backgroundColor: `${theme?.palette?.background?.main ?? '#eee'} !important`,
            height: '100%',
            minHeight: 'calc(100vh - 160px)',
          },
        }}
      />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  )
}
